import type { INTAKE_DOCUMENT_CONTENT_TYPES } from '@steward-app/steward-types';

/**
 * What a document IS, read from its first bytes, shared by the two places a person adds a file
 * to a firm's record: the onboarding form's "Requests from your advisor" step and the intake
 * statement and tax return upload (crossmarc/steward-app#2307, #2327).
 *
 * appcore's client document commit reads the first 8 bytes of the stored object and refuses a
 * file whose bytes are not the signature of the type it was declared under
 * (`ONBOARDING_DOCUMENT_CONTENT_MISMATCH`). A browser derives `File.type` from the extension, so a
 * PNG saved as `photo.jpg` would be declared a JPEG, uploaded and refused at the end. Both paths
 * therefore declare the type {@link documentTypeOfBytes} answers, never `File.type` or the
 * extension.
 *
 * The signatures are a COPY of appcore `CLIENT_DOCUMENT_SIGNATURES`
 * (`packages/core/src/db/onboarding-client-document-signature.ts`, read at appcore origin/main
 * `6b02f9729a22ca40a356eb340dd768d0bb6e7b3e` on 2026-10-13). Nothing ties them to appcore's;
 * `documentSignature.test.ts` pins THIS copy. A file this accepts that appcore refuses is
 * answered at the commit with the same sentence ({@link CONTENT_MISMATCH_MESSAGE}).
 *
 * Offset 0 only, no tolerance: a PDF with bytes before `%PDF-` is refused here as appcore refuses
 * it. This is a kindness, not a control; appcore's check decides what is kept.
 */

export type DocumentContentType = (typeof INTAKE_DOCUMENT_CONTENT_TYPES)[number];

/** How many leading bytes {@link documentTypeOfBytes} needs: the longest signature. */
export const SIGNATURE_READ_BYTES = 8;

export const DOCUMENT_SIGNATURES: ReadonlyArray<{ type: DocumentContentType; bytes: readonly number[]; extensions: readonly string[] }> = [
  { type: 'application/pdf', bytes: [0x25, 0x50, 0x44, 0x46, 0x2d], extensions: ['pdf'] },
  { type: 'image/jpeg', bytes: [0xff, 0xd8, 0xff], extensions: ['jpg', 'jpeg'] },
  { type: 'image/png', bytes: [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a], extensions: ['png'] },
];

/** The one sentence for a file whose contents are not the type it says it is: appcore's own
 *  message on the refusal, and the browser's when its own first-bytes check refuses a file whose
 *  name claims PDF, JPEG or PNG. Used by both upload paths. */
export const CONTENT_MISMATCH_MESSAGE =
  'This file\'s contents do not match its file type. Save it again as a PDF, JPEG or PNG and add it once more.';

/** The type the first bytes show, or `null`. A read shorter than the signature is `null`. */
export function documentTypeOfBytes(head: Uint8Array): DocumentContentType | null {
  for (const signature of DOCUMENT_SIGNATURES) {
    if (head.length >= signature.bytes.length && signature.bytes.every((byte, i) => head[i] === byte)) {
      return signature.type;
    }
  }
  return null;
}

/** The file's first {@link SIGNATURE_READ_BYTES} bytes. Rejects if the file cannot be read. */
export async function readFileHead(file: File): Promise<Uint8Array> {
  return new Uint8Array(await file.slice(0, SIGNATURE_READ_BYTES).arrayBuffer());
}

/** The text after the last dot of a name, or ''. */
export function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot + 1) : '';
}

/** True when the name's extension is one of the three types' (any of them). */
export function claimsADocumentType(name: string): boolean {
  const ext = extensionOf(name.trim()).toLowerCase();
  return DOCUMENT_SIGNATURES.some((s) => s.extensions.includes(ext));
}

/**
 * A name split for `type`: the part before the extension and an extension that matches the type.
 * A right extension (`.jpg` and `.jpeg` both for a JPEG) is kept as written. A wrong one is
 * replaced only when it looks like one: one to five letters and digits with at least one letter
 * (`.docx`). An all-digit tail (`Statement 12.31.2025`, `Q1.2025`) and anything else
 * (`Statement. Jan 2025`) is kept, and the right extension is added. An empty base is `Document`.
 */
export function splitNameForType(name: string, type: DocumentContentType): { base: string; extension: string } {
  const signature = DOCUMENT_SIGNATURES.find((s) => s.type === type)!;
  const current = extensionOf(name);
  let base: string;
  let extension: string;
  if (signature.extensions.includes(current.toLowerCase())) {
    base = name.slice(0, name.length - current.length - 1);
    extension = current;
  } else {
    base = /^(?=[A-Za-z0-9]{1,5}$)[0-9]*[A-Za-z]/.test(current) ? name.slice(0, name.length - current.length - 1) : name;
    extension = signature.extensions[0]!;
  }
  base = base.trim();
  if (base === '') base = 'Document';
  return { base, extension };
}
